import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  MapPin, Loader2, AlertTriangle, CheckSquare, Target,
  Shield, Users, Copy, Check, Building2
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { API } from "../App";
import { toast } from "sonner";

const CopyBtn = ({ text }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
      onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
    >
      {copied ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
};

export default function VisitPrepDialog({ leadId, leadName, open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && !data && !loading) fetchPrep();
  }, [open]);

  const fetchPrep = async () => {
    if (data) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/ai-screens/visit-prep/${leadId}`);
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to generate visit prep");
      toast.error("Failed to generate visit prep");
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (isOpen) => {
    if (isOpen) fetchPrep();
    else onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="visit-prep-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-['Manrope']">
            <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center">
              <MapPin className="w-4 h-4 text-violet-600" />
            </div>
            Visit Prep — {leadName}
          </DialogTitle>
          <p className="text-sm text-slate-500">AI-generated pitch points, buyer priorities, and checklist</p>
        </DialogHeader>

        {loading && (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
            <p className="text-sm text-slate-500">Preparing your visit brief...</p>
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 p-4 bg-red-50 rounded-lg text-red-700 text-sm">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            {error}
          </div>
        )}

        {data && !loading && (
          <div className="space-y-4 mt-2">
            {/* Project Info */}
            {data.project && (
              <div className="bg-violet-50 border border-violet-100 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Building2 className="w-4 h-4 text-violet-600" />
                  <p className="font-semibold text-violet-800">{data.project.name}</p>
                </div>
                <div className="flex flex-wrap gap-2 text-xs">
                  {data.project.location && <Badge variant="outline">{data.project.location}</Badge>}
                  {data.project.price_min && <Badge variant="outline">{(data.project.price_min / 100000).toFixed(0)}L - {(data.project.price_max / 100000).toFixed(0)}L</Badge>}
                  {data.project.possession_date && <Badge variant="outline">Possession: {data.project.possession_date}</Badge>}
                  {data.project.rera_number && <Badge variant="outline">RERA: {data.project.rera_number}</Badge>}
                </div>
              </div>
            )}

            {/* Pitch Points */}
            <div className="bg-white border border-slate-200 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <Target className="w-3.5 h-3.5 text-emerald-600" />
                  <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Pitch Points</p>
                </div>
                <CopyBtn text={data.prep?.pitch_points?.join("\n") || ""} />
              </div>
              <ul className="space-y-1.5">
                {data.prep?.pitch_points?.map((pt, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                    <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">{i + 1}</span>
                    {pt}
                  </li>
                ))}
              </ul>
            </div>

            {/* Buyer Priorities */}
            {data.prep?.buyer_priorities?.length > 0 && (
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5" /> Buyer Priorities
                </p>
                <div className="flex flex-wrap gap-2">
                  {data.prep.buyer_priorities.map((p, i) => (
                    <Badge key={i} className="bg-blue-100 text-blue-800 border-blue-200">{p}</Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Questions They May Ask */}
            {data.prep?.questions_buyer_may_ask?.length > 0 && (
              <div className="bg-amber-50 border border-amber-100 rounded-lg p-3">
                <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-2">Be Ready For</p>
                <ul className="space-y-1">
                  {data.prep.questions_buyer_may_ask.map((q, i) => (
                    <li key={i} className="text-sm text-amber-800 flex items-start gap-2">
                      <span className="text-amber-500 mt-0.5">?</span> {q}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Competitive Advantages */}
            {data.prep?.competitive_advantages?.length > 0 && (
              <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3">
                <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  <Shield className="w-3.5 h-3.5" /> Your Advantages
                </p>
                <ul className="space-y-1">
                  {data.prep.competitive_advantages.map((a, i) => (
                    <li key={i} className="text-sm text-emerald-800">+ {a}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Closing Strategy */}
            {data.prep?.closing_strategy && (
              <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wide">Closing Strategy</p>
                  <CopyBtn text={data.prep.closing_strategy} />
                </div>
                <p className="text-sm text-indigo-900">{data.prep.closing_strategy}</p>
              </div>
            )}

            {/* Checklist */}
            {data.checklist?.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-lg p-3">
                <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  <CheckSquare className="w-3.5 h-3.5 text-slate-500" /> Carry Checklist
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                  {data.checklist.map((item, i) => (
                    <label key={i} className="flex items-center gap-2 text-sm text-slate-700 py-0.5">
                      <input type="checkbox" className="rounded border-slate-300 text-violet-600 focus:ring-violet-500" />
                      {item.item}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Competitors */}
            {data.competitors?.length > 0 && (
              <div className="bg-red-50 border border-red-100 rounded-lg p-3">
                <p className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-2">Competing Projects</p>
                {data.competitors.map((c, i) => (
                  <p key={i} className="text-sm text-red-800">{c.name} by {c.developer} — {(c.price_min / 100000).toFixed(0)}L-{(c.price_max / 100000).toFixed(0)}L</p>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
